import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { useSnackbar } from 'notistack';
import { Select, Table, TableColumnsType } from 'antd';
import Pagination from '../../Pagination';
import Button from '../../Button';
import Loader from '../../Loader';
import config from '../../../lib/config';
import { infoVariant } from '../../../lib/barVariant';
import { doGetClassStudents } from '../../../store/classStudent.slice';
import { getCommonCodeLabel } from '../../../lib/store';
import StartClassDatePicker from '../../StartClassDatePicker';
import {
  IGetNotifications,
  INotificationItem,
} from '../../../models/notification.model';
import useAxios from '../../../hooks/useAxios';
import { getNotifications } from '../../../api/notification.api';
import ErrorResponse from '../../ErrorResponse';
import { IGetNewStudents } from '../../../models/students.model';
import { getNewStudents } from '../../../api/students.api';
import { createMultiStudent, createStudent } from '../../../api/campusClasses2.api';
import EduStepSelect from "../../common/EduStepSelect";
import SelectLevel from "../../statistics/e-learning/SelectLevel";
import useNotification from '../../../hooks/useNotification';
import { IResponse } from '../../../models/common.api.model';
import { IStudentReq } from '../../../models/campusClasses2.model';

interface IProps {
  setTab: (tab: string) => void;
  semesterCode: number;
}

const ClassAllocateStudent2: FC<IProps> = ({ setTab, semesterCode }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { chosenClass } = useSelector((state: RootState) => state.class);

  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetNotifications>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetNewStudents>();
  const {
    data: createRes,
    isLoading: createIsLoading,
    error: createError,
    execute: createExecute,
  } = useAxios();
  const {
    data: createMultiRes,
    isLoading: createMultiIsLoading,
    error: createMultiError,
    execute: createMultiExecute,
  } = useAxios<IResponse<String>>();

  const [field, setField] = useState('korName');
  const [keyword, setKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notificationItemList, setNotificationItemList] = useState<
    INotificationItem[]
  >([]);
  const [page, setPage] = useState(1);
  const [params, setParams] = useState<any>({ perPage: config.pageSize });
  const [record, setRecord] = useState<any>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [courseCode, setCourseCode] = useState<string>();
  const { infoNotification, successNotification, errorNotification } = useNotification();
  const [selectsKeys, setSelectsKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (chosenClass) {
      execute(getNotifications(chosenClass.classCode));
    }
  }, [chosenClass]);

  useEffect(() => {
    if (chosenClass) {
      doGetStudents();
    }
  }, [chosenClass, params, page]);

  useEffect(() => {
    if (res && !isLoading) {
      setNotificationItemList(res.data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (createRes && !createIsLoading && chosenClass) {
      dispatch(doGetClassStudents(chosenClass.classCode));
      sessionStorage.setItem('studentKorName', record.studentKorName);
      sessionStorage.setItem('memberCode', record.memberCode);
      setTab('allocateResult');
    }
  }, [createRes, createIsLoading, chosenClass]);

  useEffect(() => {
    if (createMultiRes && !createMultiIsLoading && chosenClass) {
      dispatch(doGetClassStudents(chosenClass.classCode));
      
      if(createMultiRes.result === 'success') {
        successNotification(t('common.message.save1')); 
      } else {
        errorNotification(t('common.message.error'));
      }
      
      initSelects();
      setTab('register');
    }
  }, [createMultiRes, createMultiIsLoading, chosenClass]);

  const doGetStudents = async () => {
    if (!chosenClass || !chosenClass?.semesterCode) {
      return;
    }

    execute2(
      getNewStudents(chosenClass.campusCode, Number(chosenClass.semesterCode), {
        ...params,
        pageNo: page,
      }),
    );
  };

  const onAllocate = async (record: any) => {
    const classCode = chosenClass?.classCode;
    const timetableCode = notificationItemList.find(
      (item) => item.timetableDate === startDate,
    )?.timetableCode;
    if (!classCode || !timetableCode) return;

    if (!startDate) {
      return enqueueSnackbar(t('class.class.message18'), infoVariant);
    }

    const req = {
      studentCode: record.memberCode,
      studySrtDate: startDate,
      srtTimetableCode: timetableCode,
    };

    setRecord(record);
    createExecute(createStudent(classCode, req));
  };

  const onMultiAllocate = () => {
    if(selectsKeys.length === 0) {
      return infoNotification(t('class.class.message35'));
    } else {
      const classCode = chosenClass?.classCode;
      const timetableCode = notificationItemList.find(
        (item) => item.timetableDate === startDate,
      )?.timetableCode;
      if (!classCode || !timetableCode) return;

      if (!startDate) {
        return enqueueSnackbar(t('class.class.message18'), infoVariant);
      }

      const reqData : IStudentReq[] = [];
      selectsKeys.forEach((memberCode) => {
        reqData.push({
          studentCode: memberCode.toString(),
          studySrtDate: startDate,
          srtTimetableCode: timetableCode,
        });
      });

      createMultiExecute(createMultiStudent(classCode, reqData));
    }
  }  

  const onSearch = () => {
    const params: any = {
      perPage: config.pageSize,
    };
    if (field && keyword) {
      params[field] = keyword;
    }

    if(eduStepCode) {
      params['eduStepCode'] = eduStepCode;
    }

    if(courseCode) {
      params['courseCode'] = courseCode;
    }

    setParams(params);
    setPage(1);

    initSelects();
  };

  const initSelects = () => {
    setSelectsKeys([]);
  }

  const rowSelection = {
    selectedRowKeys: selectsKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: any) => {
      setSelectsKeys(selectedRowKeys);
    },
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('class.class.column29'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      align: 'center',
      sorter: (a, b) => a.studentKorName?.localeCompare(b.studentKorName),
    },
    {
      title: t('class.class.column40'),
      dataIndex: 'studentEngName',
      key: 'studentEngName',
      align: 'center',
      sorter: (a, b) => a.studentEngName?.localeCompare(b.studentEngName),
    },
    {
      title: t('class.class.column41'),
      dataIndex: 'webId',
      key: 'webId',
      align: 'center',
      sorter: (a, b) => a.webId?.localeCompare(b.webId),
    },
    {
      title: t('class.class.column31'),
      dataIndex: 'gradeCode',
      key: 'gradeCode',
      align: 'center',
      sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
    {
      title: t('class.class.button4'),
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink"
            onClick={() => onAllocate(record)}
            text={t('class.class.button4')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {(isLoading || isLoading2 || createIsLoading || createMultiIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={createError} />
      <ErrorResponse error={createMultiError} />

      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx p-2">
            <li>
              <div>
                  <EduStepSelect
                    eduStepCode={eduStepCode}
                    setEduStepCode={value => {
                      setEduStepCode(value);
                      setCourseCode(undefined);
                    }}
                    fullWidth
                    includeAll
                  />
              </div>
              <div>
                <SelectLevel
                  courseCode={courseCode}
                  setCourseCode={setCourseCode}
                  eduStepCode={eduStepCode}                        
                  includeAll
                />
              </div>
              <div>
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={field}
                  onChange={(value) => setField(value)}
                >
                  <Select.Option value="korName">
                    {t('class.class.column29')}
                  </Select.Option>
                  <Select.Option value="engName">
                    {t('class.class.column40')}
                  </Select.Option>
                  <Select.Option value="webId">
                    {t('class.class.column41')}
                  </Select.Option>
                </Select>
              </div>
              <div>
                <input
                  type="text"
                  className="form-control"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') onSearch();
                  }}
                />
              </div>
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
          <div className="flex justify-content-center mt-2">
            🏷️ {t('class.class.text34')}
          </div>
        </div>
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink"
            onClick={onMultiAllocate}
            text={t('class.class.button16')}
          />
        </div>
        <div className='comm-gap'>
          <span className='mr-1'>{t('class.class.column33')}</span>
          <StartClassDatePicker
            notificationItemList={notificationItemList}
            startDate={startDate}
            onStartDateChange={setStartDate}
          />
        </div>
      </div>
      <div className='comm-table__area'>
        <Table
          columns={columns}
          dataSource={res2?.data}
          pagination={{ pageSize: config.pageSize }}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          rowKey="memberCode"
        />
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => setTab('register')}
            text={t('common.button.back')}
          />
        </div>
        <div className="common-paging center">
          <Pagination count={res2?.total || 0} page={page} setPage={setPage} />
        </div>
      </div>
    </>
  );
};

export default ClassAllocateStudent2;
